/**
 * Flattened public-website UI strings for static HTML + client JS + the Space viewer.
 * Catalog chrome lives in catalogUi; lists home/discover in listsUi; extras here.
 */

import { CATALOG_SITE_LOCALES, type CatalogSiteLocale } from '../constants.js';
import { catalogUi } from './catalogUi.js';
import { listsUi } from './listsUi.js';

export type { CatalogSiteLocale } from '../constants.js';
export { catalogUi } from './catalogUi.js';
export type { CatalogUiStrings } from './catalogUi.js';
export { listsUi } from './listsUi.js';
export type { ListsUiStrings } from './listsUi.js';

export type ExtraUiStrings = {
  confirm: string;
  cancel: string;
  close: string;
  save: string;
  dialogFailed: string;
  requestFailed: string;
  consentAria: string;
  consentBanner: string;
  consentAcceptAll: string;
  consentNecessaryOnly: string;
  consentCustomize: string;
  consentBack: string;
  consentDetails: string;
  consentNecessary: string;
  consentAnalytics: string;
  consentYoutube: string;
  consentPrivacyChoices: string;
  consentPrivacyPolicy: string;
  consentTerms: string;
  consentImprint: string;
  legalContactTitle: string;
  legalContactDescription: string;
  legalContactHeading: string;
  legalContactOperated: string;
  legalAbuseHeading: string;
  legalAbuseBody: string;
  legalContactHeading2: string;
  legalContactInbox: string;
  legalDsaHeading: string;
  legalDsaNotice: string;
  legalTcoNotice: string;
  legalPoliciesHeading: string;
  legalNoticeForm: string;
  legalReportTitle: string;
  legalReportDescription: string;
  legalReportHeading: string;
  legalReportIntro: string;
  legalReportUrlLabel: string;
  legalReportUrlPh: string;
  legalReportCategory: string;
  legalCatIllegal: string;
  legalCatCopyright: string;
  legalCatAbuse: string;
  legalCatPrivacy: string;
  legalCatSpam: string;
  legalCatOther: string;
  legalReportDetails: string;
  legalReportDetailsPh: string;
  legalReportName: string;
  legalReportEmail: string;
  legalReportSlug: string;
  legalReportSlugPh: string;
  legalGoodFaith: string;
  legalSubmitNotice: string;
  legalTurnstileRequired: string;
  legalNoticeReceived: string;
  loginBlurbGuest: string;
  loginBlurbSigned: string;
  loginAgeConfirm: string;
  loginContinueGoogle: string;
  loginContinueApple: string;
  loginOrEmail: string;
  loginEmailMode: string;
  loginCreateAccount: string;
  loginDisplayNameOptional: string;
  loginEmail: string;
  loginPassword: string;
  loginConfirmPassword: string;
  loginSignInEmail: string;
  loginForgotPassword: string;
  loginPublicProfile: string;
  loginPublicAlias: string;
  loginAliasTitle: string;
  loginDisplayName: string;
  loginBio: string;
  loginProfilePhoto: string;
  loginPhotoHint: string;
  loginRemovePhoto: string;
  loginProfilePublic: string;
  loginPrivateDefault: string;
  loginPublicUrl: string;
  loginCopy: string;
  loginSaveProfile: string;
  loginUnsavedChanges: string;
  loginYourSpaces: string;
  loginYourCollections: string;
  loginNewCollection: string;
  loginFavTemplates: string;
  loginShowFavOnProfile: string;
  loginAchievementsHeading: string;
  loginAchievementsHint: string;
  loginShowBadgesOnProfile: string;
  loginShowClassOnProfile: string;
  loginShowAchievementGroup: string;
  loginAchievementsLoadError: string;
  loginFeaturedHeading: string;
  loginFeaturedHint: string;
  loginFeaturedCount: string;
  loginFeaturedMax: string;
  loginAchGroupsFold: string;
  loginAchFeaturedFold: string;
  loginFavEmpty: string;
  loginPublishExport: string;
  loginZipHint: string;
  loginZipContext: string;
  loginZipContextPh: string;
  loginPublishZip: string;
  loginDeleteHeading: string;
  loginDeleteLead: string;
  loginDeleteBody: string;
  loginTypeDelete: string;
  loginDeleteConfirmPh: string;
  loginDeleteButton: string;
  loginSignedInAs: string;
  loginSignInProviders: string;
  loginAgeAttestAgain: string;
  loginPasswordsMismatch: string;
  loginPasswordMin: string;
  loginResetSent: string;
  loginDeleteAccountTitle: string;
  loginDeleteAccountBody: string;
  loginSavedPublic: string;
  loginSavedPrivate: string;
  loginRemovePhotoTitle: string;
  loginRemovePhotoBody: string;
  loginUrlCopied: string;
  loginUrlCopyFail: string;
  loginAliasFormat: string;
  loginAliasAvailable: string;
  loginAliasCurrent: string;
  loginAliasTaken: string;
  loginCropHint: string;
  loginCropFailed: string;
  loginUsePhoto: string;
  loginEnterCollectionName: string;
  loginSaving: string;
  loginCreateFailed: string;
  loginChooseZip: string;
  loginZipFailed: string;
  loginPublished: string;
  loginAuthInvalidEmail: string;
  loginAuthDisabled: string;
  loginAuthNotFound: string;
  loginAuthWrongPassword: string;
  loginAuthInvalidCred: string;
  loginAuthEmailInUse: string;
  loginAuthWeakPassword: string;
  loginAuthTooMany: string;
  loginAuthMissingPassword: string;
  loginAuthRecentLogin: string;
  loginAliasTakenErr: string;
  loginInvalidAlias: string;
  loginAliasQuota: string;
  loginNameQuota: string;
  loginHandoffFailed: string;
  cardHeartSpace: string;
  cardAddToCollection: string;
  cardReportSpace: string;
  cardShareSpace: string;
  cardReportTitle: string;
  cardReason: string;
  cardReasonSpam: string;
  cardReasonAbuse: string;
  cardReasonIllegal: string;
  cardReasonCopyright: string;
  cardReasonPrivacy: string;
  cardReasonMisleading: string;
  cardCustomReason: string;
  cardCustomReasonPh: string;
  cardDetails: string;
  cardDetailsPh: string;
  cardSubmitReport: string;
  cardEnterCustom: string;
  cardAddDetails: string;
  cardAlreadyReported: string;
  cardReportFail: string;
  cardReportThanks: string;
  cardNewCollection: string;
  cardName: string;
  cardFavourites: string;
  cardDescOptional: string;
  cardCollectionForPh: string;
  cardMakePublic: string;
  cardCreateAdd: string;
  cardCreateFail: string;
  cardAddedTo: string;
  cardAddToTitle: string;
  cardThisShare: string;
  cardCreateNewCollection: string;
  cardCouldNotSave: string;
  cardSavedCollection: string;
  cardLinkCopied: string;
  cardCopyFail: string;
  cardLoadCollectionsFail: string;
  cardCollection: string;
  dashPublished: string;
  dashPendingDiscover: string;
  dashNoSpaces: string;
  dashNoSpacesMatch: string;
  dashNoCollections: string;
  dashSearchSpaces: string;
  dashSearchSpacesLabel: string;
  dashFilterSpaces: string;
  dashFilterAll: string;
  dashFilterPending: string;
  dashFilterFeatured: string;
  dashFilterUnlisted: string;
  dashSpaceTitle: string;
  dashSpaceTitleHint: string;
  dashUseListName: string;
  dashTitleRequired: string;
  dashShowOnProfile: string;
  dashShowOnProfileHint: string;
  dashShowOnProfileUnlistedHint: string;
  dashShowOnProfileOn: string;
  dashShowOnProfileOff: string;
  dashHiddenFromProfile: string;
  dashShowOnHome: string;
  dashShowOnHomeHint: string;
  dashShowOnHomeUnlistedHint: string;
  dashShowOnHomeOn: string;
  dashShowOnHomeOff: string;
  dashHiddenFromHome: string;
  dashOpenPublic: string;
  dashOpenUnlisted: string;
  dashContext: string;
  dashPreviewThumbnail: string;
  dashPreviewChoose: string;
  dashPreviewUseDefault: string;
  dashPreviewSaved: string;
  dashPreviewEmpty: string;
  dashPreviewSaving: string;
  dashFirstPublished: string;
  dashLastUpdated: string;
  dashUpdatedShort: string;
  dashReplaceZip: string;
  dashSpaceSaved: string;
  dashDeleteSpace: string;
  dashDeleteSpaceBody: string;
  dashDeletePermanently: string;
  dashSpaceDeleted: string;
  dashChooseZip: string;
  dashReplaceSpace: string;
  dashReplaceSpaceBody: string;
  dashZipFailed: string;
  dashSpaceReplaced: string;
  dashUnlist: string;
  dashUnlisted: string;
  dashPublishedAgain: string;
  dashCollSaved: string;
  dashDeleteColl: string;
  dashDeleteCollBody: string;
  dashCollDeleted: string;
  dashRemovedFav: string;
  dashHeartFirst: string;
  dashFavName: string;
  dashFavPublic: string;
  dashFavPrivate: string;
  dashAvatarUpdated: string;
  dashPhotoRemoved: string;
  playgroundEmpty: string;
  playgroundEdit: string;
  playgroundDoneEdit: string;
  playgroundReset: string;
  playgroundSave: string;
  playgroundHint: string;
  playgroundReplay: string;
  playgroundPhysicsOff: string;
  playgroundPhysicsOn: string;
  playgroundGravity: string;
  playgroundGravityZero: string;
  playgroundDrag: string;
  playgroundBounce: string;
  playgroundCanvasHeight: string;
  playgroundThisTile: string;
  playgroundItemDrag: string;
  playgroundItemBounce: string;
  playgroundItemDensity: string;
  playgroundItemFixed: string;
  playgroundPreviewOn: string;
  playgroundPreviewOff: string;
  playgroundResize: string;
  shareYours: string;
  shareHeart: string;
  shareGoBack: string;
  shareMenuOpen: string;
  shareMenuClose: string;
  sharePathInfo: string;
  shareNoDescription: string;
  shareContaindSpaces: string;
  shareCanvasPath: string;
  shareRelatedTemplates: string;
  shareTemplatesLabel: string;
  shareNoCanvas: string;
  sharePublishedWith: string;
  shareBakeWarn: (freezeVersion: number, count: number) => string;
  shareViewOne: string;
  shareViewsMany: string;
  shareViews: (n: number) => string;
  shareCopyLink: string;
  shareCopied: string;
  shareThisPage: string;
  sharePinPinterest: string;
  sharePrevLayout: string;
  shareNextLayout: string;
  shareViewItem: string;
  shareEnterCanvas: string;
  sharePhotoViewer: string;
  shareZoom: string;
  shareZoomOut: string;
  shareResetView: string;
  shareZoomIn: string;
  shareRefocus: string;
  shareReportTitle: string;
  shareReportHint: string;
  shareReportThanks: string;
  shareSending: string;
  shareLead: string;
  shareFooterChoices: string;
  shareSubmitReport: string;
  shareCustomReasonPh: string;
  shareDetailsPh: string;
  shareReasonLegend: string;
  shareAlreadyReported: string;
  shareReportFailed: string;
  shareCustomReasonError: string;
  shareDetailsError: string;
  shareViaDevice: string;
  shareLabel: string;
  shareBack: string;
  shareReasonSpam: string;
  shareReasonAbuse: string;
  shareReasonIllegal: string;
  shareReasonCopyright: string;
  shareReasonPrivacy: string;
  shareReasonMisleading: string;
  shareReasonOther: string;
  sharePrevPhoto: string;
  shareNextPhoto: string;
  externalLinkConfirmTitle: string;
  externalLinkConfirmBody: string;
  externalLinkConfirmContinue: string;
  externalLinkConfirmCancel: string;
  inboxAria: string;
  inboxEmpty: string;
  inboxLater: string;
  inboxDismiss: string;
  inboxUnread: string;
  inboxHistory: string;
  inboxDelete: string;
};

const EN: ExtraUiStrings = {
  confirm: 'Confirm',
  cancel: 'Cancel',
  close: 'Close',
  save: 'Save',
  dialogFailed: 'Dialog UI failed to load. Refresh and try again.',
  requestFailed: 'Request failed',
  consentAria: 'Privacy choices',
  consentBanner:
    'We use necessary cookies to run this site. Optional analytics and YouTube playback need your OK. See our',
  consentAcceptAll: 'Accept all',
  consentNecessaryOnly: 'Necessary only',
  consentCustomize: 'Customize',
  consentBack: 'Back',
  consentDetails: 'Details in the Privacy Policy',
  consentNecessary: 'Necessary',
  consentAnalytics: 'Analytics',
  consentYoutube: 'YouTube',
  consentPrivacyChoices: 'Privacy choices',
  consentPrivacyPolicy: 'Privacy Policy',
  consentTerms: 'Terms',
  consentImprint: 'Imprint',
  legalContactTitle: 'Contact / DSA — Containd Spaces',
  legalContactDescription: 'Contact Containd for DSA notices, TCO orders, and legal inquiries.',
  legalContactHeading: 'Contact / DSA',
  legalContactOperated: 'Containd public Spaces are operated by',
  legalAbuseHeading: 'Abuse & illegal content notices',
  legalAbuseBody: 'or use the',
  legalContactHeading2: 'Legal contact',
  legalContactInbox: '(same inbox as abuse for this soft-launch).',
  legalDsaHeading: 'DSA / TCO',
  legalDsaNotice: 'Notices under the EU Digital Services Act:',
  legalTcoNotice: 'German Telemedia Act (TCO) removal orders: send to',
  legalPoliciesHeading: 'Policies',
  legalNoticeForm: 'public notice form',
  legalReportTitle: 'Report illegal content — Containd Spaces',
  legalReportDescription: 'Notify Containd about allegedly illegal content on containd.space.',
  legalReportHeading: 'Report illegal content',
  legalReportIntro:
    'Use this form to notify Containd (Next Level Order Engineering UG) about allegedly illegal content on containd.space. We also accept notices at',
  legalReportUrlLabel: 'URL of the content',
  legalReportUrlPh: 'https://containd.space/s/…',
  legalReportCategory: 'Category',
  legalCatIllegal: 'Allegedly illegal content',
  legalCatCopyright: 'Copyright / IP',
  legalCatAbuse: 'Abuse / harassment',
  legalCatPrivacy: 'Privacy / personal data',
  legalCatSpam: 'Spam / scam',
  legalCatOther: 'Other',
  legalReportDetails: 'Explanation / substantiation',
  legalReportDetailsPh: 'Describe why the content is illegal or otherwise actionable…',
  legalReportName: 'Your name',
  legalReportEmail: 'Your email',
  legalReportSlug: 'Share slug (optional)',
  legalReportSlugPh: 'from /s/{slug}/',
  legalGoodFaith:
    'I have a good-faith belief that the information in this notice is accurate, and I am authorized to submit it (where applicable).',
  legalSubmitNotice: 'Submit notice',
  legalTurnstileRequired: 'Please complete the verification check.',
  legalNoticeReceived: 'Notice received. We emailed a confirmation to',
  loginBlurbGuest:
    'This is your Containd account — the same identity used in the Containd app (iOS, Android, and web) and on containd.space and the catalog. Sign in with Google, Apple, or email. Creating an account here creates the same Containd account.',
  loginBlurbSigned:
    'This is your Containd account — the same identity used in the Containd app on every platform and on containd.space and the catalog.',
  loginAgeConfirm: 'I confirm I am 16 or older.',
  loginContinueGoogle: 'Continue with Google',
  loginContinueApple: 'Continue with Apple',
  loginOrEmail: 'or email',
  loginEmailMode: 'Email mode',
  loginCreateAccount: 'Create account',
  loginDisplayNameOptional: 'Display name (optional)',
  loginEmail: 'Email',
  loginPassword: 'Password',
  loginConfirmPassword: 'Confirm password',
  loginSignInEmail: 'Sign in with email',
  loginForgotPassword: 'Forgot password?',
  loginPublicProfile: 'Public profile',
  loginPublicAlias: 'Public alias (URL slug)',
  loginAliasTitle: '2–32 characters: lowercase letters, numbers, hyphens',
  loginDisplayName: 'Display name',
  loginBio: 'Bio',
  loginProfilePhoto: 'Profile photo',
  loginPhotoHint:
    'One 4:5 portrait photo (400×500). Uploading again replaces the previous photo. The account menu uses a round crop of the same image.',
  loginRemovePhoto: 'Remove photo',
  loginProfilePublic: 'Profile is public',
  loginPrivateDefault: 'Private by default. Public profiles appear at',
  loginPublicUrl: 'Public profile URL',
  loginCopy: 'Copy',
  loginSaveProfile: 'Save profile',
  loginUnsavedChanges: 'Unsaved changes',
  loginYourSpaces: 'Your Spaces',
  loginYourCollections: 'Your collections',
  loginNewCollection: 'New collection',
  loginFavTemplates: 'Favourite templates',
  loginShowFavOnProfile: 'Show favourite templates on my public profile',
  loginAchievementsHeading: 'Achievements on your profile',
  loginAchievementsHint: 'Off by default. Only image and name are shown — never how to unlock.',
  loginShowBadgesOnProfile: 'Show badges on my public profile',
  loginShowClassOnProfile: 'Show class on my public profile',
  loginShowAchievementGroup: 'Show group:',
  loginAchievementsLoadError: 'Could not load achievement settings. Refresh and try again.',
  loginFeaturedHeading: 'Featured beside your photo',
  loginFeaturedHint: 'Choose up to 18 round badges for the header grid (3 per row). Tap a badge in the full list below your profile to learn more.',
  loginFeaturedCount: '{{count}} of {{max}} featured',
  loginFeaturedMax: 'You can feature at most 18 badges.',
  loginAchGroupsFold: 'Visible badge groups',
  loginAchFeaturedFold: 'Featured badges',
  loginFavEmpty: 'Heart a template in the catalog to save it here.',
  loginPublishExport: 'Publish from export',
  loginZipHint:
    'Upload a Containd export ZIP (share/manifest.json or freeze.json). Creates a new public page. To update an existing page, select it above and use Replace from ZIP — that keeps the title and link and requires new Discover approval.',
  loginZipContext: 'Context description (#hashtags ok)',
  loginZipContextPh: 'What this Space is for…',
  loginPublishZip: 'Publish ZIP',
  loginDeleteHeading: 'Delete Containd account',
  loginDeleteLead: 'This deletes your Containd account',
  loginDeleteBody:
    'It permanently removes all server data for this account: cloud sync, public Spaces, profile, avatar, collections, uploaded files, AI requests and credits, analytics identity, and push registration. Billing records may be kept up to 32 days, then purged. Deleting one published Space above is different; this removes the whole account. This cannot be undone. The same email may register again. If you also use the Containd app, lists stored only on the device are unchanged.',
  loginTypeDelete: 'Type DELETE to confirm',
  loginDeleteConfirmPh: 'DELETE',
  loginDeleteButton: 'Permanently delete account',
  loginSignedInAs: 'Signed in as',
  loginSignInProviders: 'Sign in with Google, Apple, or email to manage your public profile.',
  loginAgeAttestAgain: 'Confirm you are 16 or older, then sign in again.',
  loginPasswordsMismatch: 'Passwords do not match.',
  loginPasswordMin: 'Password must be at least 6 characters.',
  loginResetSent: 'Password reset email sent (if that address has an account).',
  loginDeleteAccountTitle: 'Delete account',
  loginDeleteAccountBody:
    'This permanently deletes your Containd account and server data. Type DELETE to confirm.',
  loginSavedPublic: 'Saved. Public page:',
  loginSavedPrivate: 'Saved. Profile stays private until you check “Profile is public”.',
  loginRemovePhotoTitle: 'Remove photo',
  loginRemovePhotoBody: 'Remove your profile photo?',
  loginUrlCopied: 'Profile URL copied.',
  loginUrlCopyFail: 'Could not copy URL.',
  loginAliasFormat: 'Use 2–32 lowercase letters, numbers, or hyphens.',
  loginAliasAvailable: 'Available',
  loginAliasCurrent: 'This is your current username.',
  loginAliasTaken: 'That username is taken.',
  loginCropHint: 'Drag to frame a 4:5 portrait photo',
  loginCropFailed: 'Avatar crop UI failed to load. Refresh and try again.',
  loginUsePhoto: 'Use photo',
  loginEnterCollectionName: 'Enter a collection name.',
  loginSaving: 'Saving…',
  loginCreateFailed: 'Failed to create collection',
  loginChooseZip: 'Sign in and choose a ZIP first.',
  loginZipFailed: 'ZIP publisher failed to load. Refresh and try again.',
  loginPublished: 'Published:',
  loginAuthInvalidEmail: 'Enter a valid email address.',
  loginAuthDisabled: 'This account has been disabled.',
  loginAuthNotFound: 'No account found for that email.',
  loginAuthWrongPassword: 'Incorrect password.',
  loginAuthInvalidCred: 'Incorrect email or password.',
  loginAuthEmailInUse: 'An account already exists with that email. Sign in instead.',
  loginAuthWeakPassword: 'Password must be at least 6 characters.',
  loginAuthTooMany: 'Too many attempts. Try again later.',
  loginAuthMissingPassword: 'Enter a password.',
  loginAuthRecentLogin:
    'For security, sign out, sign in again, then try deleting your account.',
  loginAliasTakenErr: 'That username is taken. Try another.',
  loginInvalidAlias: 'Alias must be 2–32 characters: lowercase letters, numbers, hyphens.',
  loginAliasQuota: 'Alias can change twice every 90 days. Try again later.',
  loginNameQuota: 'Display name can change four times every 30 days. Try again later.',
  loginHandoffFailed: 'Signed in on containd.space, but catalog handoff failed:',
  cardHeartSpace: 'Heart this Space',
  cardAddToCollection: 'Add to collection…',
  cardReportSpace: 'Report this Space',
  cardShareSpace: 'Share Space',
  cardReportTitle: 'Report this Space',
  cardReason: 'Reason',
  cardReasonSpam: 'Spam or advertising',
  cardReasonAbuse: 'Harassment or abuse',
  cardReasonIllegal: 'Illegal content',
  cardReasonCopyright: 'Copyright / IP infringement',
  cardReasonPrivacy: 'Privacy violation',
  cardReasonMisleading: 'Misleading or scam',
  cardCustomReason: 'Describe the reason',
  cardCustomReasonPh: 'Short label…',
  cardDetails: 'Details',
  cardDetailsPh: 'What should moderators know?',
  cardSubmitReport: 'Submit report',
  cardEnterCustom: 'Enter a short custom reason.',
  cardAddDetails: 'Please add a few details for moderators.',
  cardAlreadyReported: 'You already reported this recently.',
  cardReportFail: 'Could not submit report.',
  cardReportThanks: 'Thanks — report submitted.',
  cardNewCollection: 'New collection',
  cardName: 'Name',
  cardFavourites: 'Favourites',
  cardDescOptional: 'Description (optional, #hashtags ok)',
  cardCollectionForPh: 'What this collection is for…',
  cardMakePublic: 'Make collection public on my profile',
  cardCreateAdd: 'Create & add',
  cardCreateFail: 'Could not create collection.',
  cardAddedTo: 'Added to',
  cardAddToTitle: 'Add to collection',
  cardThisShare: 'this share',
  cardCreateNewCollection: 'Create new collection…',
  cardCouldNotSave: 'Could not save.',
  cardSavedCollection: 'Saved to your collection.',
  cardLinkCopied: 'Link copied',
  cardCopyFail: 'Could not copy link',
  cardLoadCollectionsFail: 'Could not load your collections.',
  cardCollection: 'Collection',
  dashPublished: 'Published',
  dashPendingDiscover: 'Published · pending Discover',
  dashNoSpaces: 'No Spaces yet — publish from the Containd app.',
  dashNoSpacesMatch: 'No Spaces match — clear search or filters.',
  dashNoCollections: 'No collections yet — add a Space from Discover.',
  dashSearchSpaces: 'Search by title or slug',
  dashSearchSpacesLabel: 'Search Spaces',
  dashFilterSpaces: 'Filter Spaces',
  dashFilterAll: 'All',
  dashFilterPending: 'Pending',
  dashFilterFeatured: 'Featured',
  dashFilterUnlisted: 'Unlisted',
  dashSpaceTitle: 'Title',
  dashSpaceTitleHint:
    'Changing the title may require Discover re-approval. The public link stays the same.',
  dashUseListName: 'Use list name',
  dashTitleRequired: 'Enter a title.',
  dashShowOnProfile: 'Show on profile',
  dashShowOnProfileHint:
    'When off, this Space stays public but does not appear on your profile.',
  dashShowOnProfileUnlistedHint:
    'Unlisted Spaces never appear on your profile. Turn this on so it shows after you publish.',
  dashShowOnProfileOn: 'Shown on your profile',
  dashShowOnProfileOff: 'Hidden from your profile',
  dashHiddenFromProfile: 'Hidden from profile',
  dashShowOnHome: 'Show on home page',
  dashShowOnHomeHint:
    'When off, this Space stays public and in Discover, but does not appear on the containd.space home page.',
  dashShowOnHomeUnlistedHint:
    'Unlisted Spaces never appear on the home page. Turn this on so it shows after you publish.',
  dashShowOnHomeOn: 'Shown on the home page',
  dashShowOnHomeOff: 'Hidden from the home page',
  dashHiddenFromHome: 'Published · hidden from home',
  dashOpenPublic: 'Open public link',
  dashOpenUnlisted: 'Open unlisted link',
  dashContext: 'Context description (#hashtags ok)',
  dashPreviewThumbnail: 'Preview thumbnail',
  dashPreviewChoose: 'Choose image',
  dashPreviewUseDefault: 'Use list default',
  dashPreviewSaved: 'Preview thumbnail saved',
  dashPreviewEmpty: 'No photos in this Space yet. Add images in the app and republish.',
  dashPreviewSaving: 'Saving…',
  dashFirstPublished: 'First published',
  dashLastUpdated: 'Last updated',
  dashUpdatedShort: 'Updated {date}',
  dashReplaceZip: 'Replace from ZIP',
  dashSpaceSaved: 'Space saved',
  dashDeleteSpace: 'Delete Space',
  dashDeleteSpaceBody: 'Delete this Space permanently? The public slug cannot be reused.',
  dashDeletePermanently: 'Delete permanently',
  dashSpaceDeleted: 'Space deleted',
  dashChooseZip: 'Choose a ZIP first.',
  dashReplaceSpace: 'Replace Space',
  dashReplaceSpaceBody:
    'Replace all content of this Space? The title and link stay the same. Discover listing will need new approval.',
  dashZipFailed: 'ZIP publisher failed to load. Refresh and try again.',
  dashSpaceReplaced: 'Space replaced. Discover listing needs re-approval.',
  dashUnlist: 'Unlist',
  dashUnlisted: 'Space unlisted (still online).',
  dashPublishedAgain: 'Space published again.',
  dashCollSaved: 'Collection saved',
  dashDeleteColl: 'Delete collection',
  dashDeleteCollBody: 'Delete this collection?',
  dashCollDeleted: 'Collection deleted',
  dashRemovedFav: 'Removed from favourites',
  dashHeartFirst: 'Heart a template first, then you can show favourites on your profile.',
  dashFavName: 'Favourite templates',
  dashFavPublic: 'Favourite templates will appear on your public profile.',
  dashFavPrivate: 'Favourite templates stay private.',
  dashAvatarUpdated: 'Avatar updated',
  dashPhotoRemoved: 'Profile photo removed',
  playgroundEmpty: 'No public Spaces yet.',
  playgroundEdit: 'Edit layout',
  playgroundDoneEdit: 'Done editing',
  playgroundReset: 'Reset to grid',
  playgroundSave: 'Save layout',
  playgroundHint:
    'Drag to place. Bottom-right handle resizes freely; tap it to rotate. Drag the orange center handle to draw start speed. Preview physics to see gravity, bounce, and density live.',
  playgroundReplay: 'Reset positions',
  playgroundPhysicsOff: 'Disable physics',
  playgroundPhysicsOn: 'Enable physics',
  playgroundGravity: 'Gravity',
  playgroundGravityZero: 'Zero',
  playgroundDrag: 'Water drag',
  playgroundBounce: 'Bounce',
  playgroundCanvasHeight: 'Canvas height',
  playgroundThisTile: 'Selected Space',
  playgroundItemDrag: 'Tile drag',
  playgroundItemBounce: 'Material bounce',
  playgroundItemDensity: 'Density',
  playgroundItemFixed: 'Fixed obstacle',
  playgroundPreviewOn: 'Preview physics',
  playgroundPreviewOff: 'Pause preview',
  playgroundResize: 'Resize or rotate',
  shareYours: 'Share yours with',
  shareHeart: 'Heart this Space',
  shareGoBack: 'Go back one level',
  shareMenuOpen: 'Menu',
  shareMenuClose: 'Close menu',
  sharePathInfo: 'Space info',
  shareNoDescription: 'No description',
  shareContaindSpaces: 'Containd Spaces',
  shareCanvasPath: 'Canvas path',
  shareRelatedTemplates: 'Related templates',
  shareTemplatesLabel: 'Templates:',
  shareNoCanvas: 'No canvas data for this level.',
  sharePublishedWith: 'Published with',
  shareBakeWarn: (freezeVersion, count) =>
    `This share is freeze v${freezeVersion} but ${count} templated item${
      count === 1 ? '' : 's'
    } have no bakedDescriptors — they paint as generic tiles. Re-export from Containd (share sidecar must bake templates), then re-import.`,
  shareViewOne: '1 view',
  shareViewsMany: '{n} views',
  shareViews: (n) => `${n} views`,
  shareCopyLink: 'Copy link',
  shareCopied: 'Copied',
  shareThisPage: 'Share this page',
  sharePinPinterest: 'Pin on Pinterest',
  sharePrevLayout: 'Previous layout',
  shareNextLayout: 'Next layout',
  shareViewItem: 'View item',
  shareEnterCanvas: 'Enter canvas',
  sharePhotoViewer: 'Photo viewer',
  shareZoom: 'Canvas zoom',
  shareZoomOut: 'Zoom out',
  shareResetView: 'Reset view',
  shareZoomIn: 'Zoom in',
  shareRefocus: 'Refocus on content',
  shareReportTitle: 'Report this Space',
  shareReportHint:
    'Reports are reviewed by Containd. Your account is recorded with the report.',
  shareReportThanks: 'Thanks — we received your report for',
  shareSending: 'Sending…',
  shareLead: 'Shared Spaces from Containd',
  shareFooterChoices: 'Privacy choices',
  shareSubmitReport: 'Submit report',
  shareCustomReasonPh: 'Short custom reason…',
  shareDetailsPh: 'Details for moderators…',
  shareReasonLegend: 'Reason',
  shareAlreadyReported: 'You already reported this recently.',
  shareReportFailed: 'Could not send report. Try again after signing in.',
  shareCustomReasonError: 'Enter a short custom reason.',
  shareDetailsError: 'Please add a few details for moderators.',
  shareViaDevice: 'Share via device…',
  shareLabel: 'Share',
  shareBack: '← Back',
  shareReasonSpam: 'Spam or advertising',
  shareReasonAbuse: 'Harassment or abuse',
  shareReasonIllegal: 'Illegal content',
  shareReasonCopyright: 'Copyright / IP infringement',
  shareReasonPrivacy: 'Privacy violation',
  shareReasonMisleading: 'Misleading or scam',
  shareReasonOther: 'Other',
  sharePrevPhoto: 'Previous photo',
  shareNextPhoto: 'Next photo',
  externalLinkConfirmTitle: 'Leave this Space?',
  externalLinkConfirmBody:
    'You are about to open an external link on {host}. This site is not part of Containd Spaces.',
  externalLinkConfirmContinue: 'Continue',
  externalLinkConfirmCancel: 'Cancel',
  inboxAria: 'Inbox',
  inboxEmpty: 'No messages',
  inboxLater: 'Later',
  inboxDismiss: 'Dismiss',
  inboxUnread: 'Unread',
  inboxHistory: 'Earlier',
  inboxDelete: 'Delete',
};

const DE: ExtraUiStrings = {
  ...EN,
  confirm: 'Bestätigen',
  cancel: 'Abbrechen',
  close: 'Schließen',
  save: 'Speichern',
  dialogFailed: 'Dialog konnte nicht geladen werden. Bitte neu laden.',
  requestFailed: 'Anfrage fehlgeschlagen',
  consentAria: 'Datenschutz-Einstellungen',
  consentBanner:
    'Wir verwenden notwendige Cookies, um diese Website zu betreiben. Optionale Analysen und YouTube-Wiedergabe brauchen Ihre Zustimmung. Siehe',
  consentAcceptAll: 'Alle akzeptieren',
  consentNecessaryOnly: 'Nur notwendige',
  consentCustomize: 'Anpassen',
  consentBack: 'Zurück',
  consentDetails: 'Details in der Datenschutzerklärung',
  consentNecessary: 'Notwendig',
  consentAnalytics: 'Analyse',
  consentYoutube: 'YouTube',
  consentPrivacyChoices: 'Datenschutz-Einstellungen',
  consentPrivacyPolicy: 'Datenschutzerklärung',
  consentTerms: 'Nutzungsbedingungen',
  consentImprint: 'Impressum',
  legalContactTitle: 'Kontakt / DSA — Containd Spaces',
  legalContactDescription: 'Kontakt zu Containd für DSA-Meldungen, TCO-Anordnungen und rechtliche Anfragen.',
  legalContactHeading: 'Kontakt / DSA',
  legalContactOperated: 'Die öffentlichen Containd-Spaces werden betrieben von',
  legalAbuseHeading: 'Missbrauch und illegale Inhalte',
  legalAbuseBody: 'oder nutzen Sie das',
  legalContactHeading2: 'Rechtlicher Kontakt',
  legalContactInbox: '(dieselbe Inbox wie für Missbrauch in diesem Soft-Launch).',
  legalDsaHeading: 'DSA / TCO',
  legalDsaNotice: 'Meldungen nach dem EU-Gesetz über digitale Dienste:',
  legalTcoNotice: 'Löschanordnungen nach deutschem Telemediengesetz (TCO) an',
  legalPoliciesHeading: 'Richtlinien',
  legalNoticeForm: 'öffentliche Meldeformular',
  legalReportTitle: 'Illegale Inhalte melden — Containd Spaces',
  legalReportDescription: 'Containd über mutmaßlich illegale Inhalte auf containd.space informieren.',
  legalReportHeading: 'Illegale Inhalte melden',
  legalReportIntro:
    'Mit diesem Formular benachrichtigen Sie Containd (Next Level Order Engineering UG) über mutmaßlich illegale Inhalte auf containd.space. Meldungen auch an',
  legalReportUrlLabel: 'URL des Inhalts',
  legalReportCategory: 'Kategorie',
  legalCatIllegal: 'Mutmaßlich illegaler Inhalt',
  legalCatCopyright: 'Urheberrecht / IP',
  legalCatAbuse: 'Missbrauch / Belästigung',
  legalCatPrivacy: 'Datenschutz / personenbezogene Daten',
  legalCatSpam: 'Spam / Betrug',
  legalCatOther: 'Sonstiges',
  legalReportDetails: 'Erläuterung / Begründung',
  legalReportDetailsPh: 'Beschreiben Sie, warum der Inhalt illegal oder sonstig zu beanstanden ist…',
  legalReportName: 'Ihr Name',
  legalReportEmail: 'Ihre E-Mail',
  legalReportSlug: 'Share-Slug (optional)',
  legalGoodFaith:
    'Ich bin in gutem Glauben, dass die Angaben in dieser Meldung zutreffen, und bin berechtigt, sie einzureichen (soweit erforderlich).',
  legalSubmitNotice: 'Meldung senden',
  legalTurnstileRequired: 'Bitte die Prüfung abschließen.',
  legalNoticeReceived: 'Meldung erhalten. Eine Bestätigung ging an',
  loginBlurbGuest:
    'Das ist Ihr Containd-Konto — dieselbe Identität wie in der Containd-App (iOS, Android und Web) sowie auf containd.space und im Katalog. Melden Sie sich mit Google, Apple oder E-Mail an. Ein Konto hier ist dasselbe Containd-Konto.',
  loginBlurbSigned:
    'Das ist Ihr Containd-Konto — dieselbe Identität wie in der Containd-App auf jeder Plattform sowie auf containd.space und im Katalog.',
  loginAgeConfirm: 'Ich bestätige, dass ich 16 Jahre oder älter bin.',
  loginContinueGoogle: 'Weiter mit Google',
  loginContinueApple: 'Weiter mit Apple',
  loginOrEmail: 'oder E-Mail',
  loginEmailMode: 'E-Mail-Modus',
  loginCreateAccount: 'Konto erstellen',
  loginDisplayNameOptional: 'Anzeigename (optional)',
  loginEmail: 'E-Mail',
  loginPassword: 'Passwort',
  loginConfirmPassword: 'Passwort bestätigen',
  loginSignInEmail: 'Mit E-Mail anmelden',
  loginForgotPassword: 'Passwort vergessen?',
  loginPublicProfile: 'Öffentliches Profil',
  loginPublicAlias: 'Öffentlicher Alias (URL-Slug)',
  loginAliasTitle: '2–32 Zeichen: Kleinbuchstaben, Zahlen, Bindestriche',
  loginDisplayName: 'Anzeigename',
  loginBio: 'Steckbrief',
  loginProfilePhoto: 'Profilfoto',
  loginPhotoHint:
    'Ein Hochformat 4:5 (400×500). Ein erneuter Upload ersetzt das bisherige Foto. Das Kontomenü nutzt einen runden Ausschnitt desselben Bildes.',
  loginRemovePhoto: 'Foto entfernen',
  loginProfilePublic: 'Profil ist öffentlich',
  loginPrivateDefault: 'Standardmäßig privat. Öffentliche Profile erscheinen unter',
  loginPublicUrl: 'Öffentliche Profil-URL',
  loginCopy: 'Kopieren',
  loginSaveProfile: 'Profil speichern',
  loginUnsavedChanges: 'Ungespeicherte Änderungen',
  loginYourSpaces: 'Ihre Spaces',
  loginYourCollections: 'Ihre Sammlungen',
  loginNewCollection: 'Neue Sammlung',
  loginFavTemplates: 'Lieblingsvorlagen',
  loginShowFavOnProfile: 'Lieblingsvorlagen auf meinem öffentlichen Profil zeigen',
  loginAchievementsHeading: 'Erfolge auf deinem Profil',
  loginAchievementsHint: 'Standardmäßig aus. Es werden nur Bild und Name angezeigt — nie die Freischaltbedingung.',
  loginShowBadgesOnProfile: 'Abzeichen auf meinem öffentlichen Profil zeigen',
  loginShowClassOnProfile: 'Klasse auf meinem öffentlichen Profil zeigen',
  loginShowAchievementGroup: 'Gruppe anzeigen:',
  loginAchievementsLoadError: 'Erfolg-Einstellungen konnten nicht geladen werden. Seite neu laden und erneut versuchen.',
  loginFeaturedHeading: 'Neben deinem Foto hervorgehoben',
  loginFeaturedHint: 'Wähle bis zu 18 runde Abzeichen für das Kopfzeilen-Raster (3 pro Zeile).',
  loginFeaturedCount: '{{count}} von {{max}} hervorgehoben',
  loginFeaturedMax: 'Du kannst höchstens 18 Abzeichen hervorheben.',
  loginAchGroupsFold: 'Sichtbare Abzeichen-Gruppen',
  loginAchFeaturedFold: 'Hervorgehobene Abzeichen',
  loginFavEmpty: 'Merken Sie eine Vorlage im Katalog, um sie hier zu speichern.',
  loginPublishExport: 'Aus Export veröffentlichen',
  loginZipHint:
    'Laden Sie eine Containd-Export-ZIP hoch (share/manifest.json oder freeze.json). Erstellt eine neue öffentliche Seite. Zum Aktualisieren wählen Sie oben die Seite und Nutzen Sie ZIP ersetzen — Titel und Link bleiben, Discover braucht eine neue Freigabe.',
  loginZipContext: 'Kontextbeschreibung (#Hashtags erlaubt)',
  loginZipContextPh: 'Wofür dieser Space ist…',
  loginPublishZip: 'ZIP veröffentlichen',
  loginDeleteHeading: 'Containd-Konto löschen',
  loginDeleteLead: 'Damit löschen Sie Ihr Containd-Konto',
  loginDeleteBody:
    'Alle Serverdaten dieses Kontos werden dauerhaft entfernt: Cloud-Sync, öffentliche Spaces, Profil, Avatar, Sammlungen, Dateien, KI-Anfragen und Guthaben, Analyse-Identität und Push-Registrierung. Abrechnungsdaten können bis zu 32 Tage aufbewahrt und dann gelöscht werden. Das Löschen eines einzelnen Spaces oben ist etwas anderes; hier wird das ganze Konto entfernt. Das kann nicht rückgängig gemacht werden. Dieselbe E-Mail kann sich erneut registrieren. Listen, die nur auf dem Gerät liegen, bleiben unverändert.',
  loginTypeDelete: 'DELETE eingeben zum Bestätigen',
  loginDeleteButton: 'Konto endgültig löschen',
  loginSignedInAs: 'Angemeldet als',
  loginSignInProviders: 'Mit Google, Apple oder E-Mail anmelden, um das öffentliche Profil zu verwalten.',
  loginAgeAttestAgain: 'Bestätigen Sie, dass Sie 16 oder älter sind, und melden Sie sich erneut an.',
  loginPasswordsMismatch: 'Passwörter stimmen nicht überein.',
  loginPasswordMin: 'Das Passwort muss mindestens 6 Zeichen haben.',
  loginResetSent: 'E-Mail zum Zurücksetzen wurde gesendet (falls ein Konto existiert).',
  loginDeleteAccountTitle: 'Konto löschen',
  loginDeleteAccountBody:
    'Damit werden Ihr Containd-Konto und die Serverdaten dauerhaft gelöscht. Geben Sie DELETE ein.',
  loginSavedPublic: 'Gespeichert. Öffentliche Seite:',
  loginSavedPrivate: 'Gespeichert. Das Profil bleibt privat, bis „Profil ist öffentlich“ aktiv ist.',
  loginRemovePhotoTitle: 'Foto entfernen',
  loginRemovePhotoBody: 'Profilfoto entfernen?',
  loginUrlCopied: 'Profil-URL kopiert.',
  loginUrlCopyFail: 'URL konnte nicht kopiert werden.',
  loginAliasFormat: '2–32 Kleinbuchstaben, Zahlen oder Bindestriche.',
  loginAliasAvailable: 'Verfügbar',
  loginAliasCurrent: 'Das ist Ihr aktueller Benutzername.',
  loginAliasTaken: 'Dieser Benutzername ist vergeben.',
  loginCropHint: 'Ziehen, um ein 4:5-Hochformat zu rahmen',
  loginCropFailed: 'Zuschnitt konnte nicht geladen werden. Bitte neu laden.',
  loginUsePhoto: 'Foto übernehmen',
  loginEnterCollectionName: 'Geben Sie einen Sammlungsnamen ein.',
  loginSaving: 'Speichern…',
  loginCreateFailed: 'Sammlung konnte nicht erstellt werden',
  loginChooseZip: 'Anmelden und zuerst eine ZIP wählen.',
  loginZipFailed: 'ZIP-Publisher konnte nicht geladen werden. Bitte neu laden.',
  loginPublished: 'Veröffentlicht:',
  loginAuthInvalidEmail: 'Gültige E-Mail-Adresse eingeben.',
  loginAuthDisabled: 'Dieses Konto wurde deaktiviert.',
  loginAuthNotFound: 'Kein Konto für diese E-Mail.',
  loginAuthWrongPassword: 'Falsches Passwort.',
  loginAuthInvalidCred: 'Falsche E-Mail oder falsches Passwort.',
  loginAuthEmailInUse: 'Es gibt bereits ein Konto mit dieser E-Mail. Stattdessen anmelden.',
  loginAuthWeakPassword: 'Das Passwort muss mindestens 6 Zeichen haben.',
  loginAuthTooMany: 'Zu viele Versuche. Später erneut versuchen.',
  loginAuthMissingPassword: 'Passwort eingeben.',
  loginAuthRecentLogin:
    'Aus Sicherheitsgründen abmelden, erneut anmelden, dann das Konto löschen.',
  loginAliasTakenErr: 'Dieser Benutzername ist vergeben. Bitte einen anderen wählen.',
  loginInvalidAlias: 'Alias: 2–32 Zeichen, Kleinbuchstaben, Zahlen, Bindestriche.',
  loginAliasQuota: 'Der Alias kann zweimal in 90 Tagen geändert werden. Später erneut versuchen.',
  loginNameQuota: 'Der Anzeigename kann viermal in 30 Tagen geändert werden. Später erneut versuchen.',
  loginHandoffFailed: 'Auf containd.space angemeldet, Katalog-Übergabe fehlgeschlagen:',
  cardHeartSpace: 'Diesen Space merken',
  cardAddToCollection: 'Zur Sammlung hinzufügen…',
  cardReportSpace: 'Diesen Space melden',
  cardShareSpace: 'Space teilen',
  cardReportTitle: 'Diesen Space melden',
  cardReason: 'Grund',
  cardReasonSpam: 'Spam oder Werbung',
  cardReasonAbuse: 'Belästigung oder Missbrauch',
  cardReasonIllegal: 'Illegaler Inhalt',
  cardReasonCopyright: 'Urheberrecht / IP-Verletzung',
  cardReasonPrivacy: 'Datenschutzverletzung',
  cardReasonMisleading: 'Irreführung oder Betrug',
  cardCustomReason: 'Grund beschreiben',
  cardCustomReasonPh: 'Kurzes Label…',
  cardDetails: 'Details',
  cardDetailsPh: 'Was sollen Moderatoren wissen?',
  cardSubmitReport: 'Meldung senden',
  cardEnterCustom: 'Kurzen eigenen Grund eingeben.',
  cardAddDetails: 'Bitte ein paar Details für Moderatoren ergänzen.',
  cardAlreadyReported: 'Sie haben das kürzlich bereits gemeldet.',
  cardReportFail: 'Meldung konnte nicht gesendet werden.',
  cardReportThanks: 'Danke — Meldung gesendet.',
  cardNewCollection: 'Neue Sammlung',
  cardName: 'Name',
  cardFavourites: 'Favoriten',
  cardDescOptional: 'Beschreibung (optional, #Hashtags erlaubt)',
  cardCollectionForPh: 'Wofür diese Sammlung ist…',
  cardMakePublic: 'Sammlung auf meinem Profil veröffentlichen',
  cardCreateAdd: 'Erstellen und hinzufügen',
  cardCreateFail: 'Sammlung konnte nicht erstellt werden.',
  cardAddedTo: 'Hinzugefügt zu',
  cardAddToTitle: 'Zur Sammlung hinzufügen',
  cardThisShare: 'dieser Freigabe',
  cardCreateNewCollection: 'Neue Sammlung erstellen…',
  cardCouldNotSave: 'Speichern fehlgeschlagen.',
  cardSavedCollection: 'In Ihrer Sammlung gespeichert.',
  cardLinkCopied: 'Link kopiert',
  cardCopyFail: 'Link konnte nicht kopiert werden',
  cardLoadCollectionsFail: 'Sammlungen konnten nicht geladen werden.',
  cardCollection: 'Sammlung',
  dashPublished: 'Veröffentlicht',
  dashPendingDiscover: 'Veröffentlicht · Discover ausstehend',
  dashNoSpaces: 'Noch keine Spaces — in der Containd-App veröffentlichen.',
  dashNoSpacesMatch: 'Keine Spaces gefunden — Suche oder Filter zurücksetzen.',
  dashNoCollections: 'Noch keine Sammlungen — einen Space aus Entdecken hinzufügen.',
  dashSearchSpaces: 'Nach Titel oder Slug suchen',
  dashSearchSpacesLabel: 'Spaces suchen',
  dashFilterSpaces: 'Spaces filtern',
  dashFilterAll: 'Alle',
  dashFilterPending: 'Ausstehend',
  dashFilterFeatured: 'Empfohlen',
  dashFilterUnlisted: 'Nicht gelistet',
  dashSpaceTitle: 'Titel',
  dashSpaceTitleHint:
    'Eine Titeländerung kann eine neue Discover-Freigabe erfordern. Der öffentliche Link bleibt gleich.',
  dashUseListName: 'Listennamen verwenden',
  dashTitleRequired: 'Bitte einen Titel eingeben.',
  dashShowOnProfile: 'Auf dem Profil zeigen',
  dashShowOnProfileHint:
    'Wenn aus, bleibt dieser Space öffentlich, erscheint aber nicht auf deinem Profil.',
  dashShowOnProfileUnlistedHint:
    'Nicht gelistete Spaces erscheinen nie auf dem Profil. Einschalten, damit er nach dem Veröffentlichen sichtbar wird.',
  dashShowOnProfileOn: 'Auf dem Profil sichtbar',
  dashShowOnProfileOff: 'Vom Profil ausgeblendet',
  dashHiddenFromProfile: 'Vom Profil ausgeblendet',
  dashShowOnHome: 'Auf der Startseite zeigen',
  dashShowOnHomeHint:
    'Wenn aus, bleibt dieser Space öffentlich und in Discover, erscheint aber nicht auf der containd.space-Startseite.',
  dashShowOnHomeUnlistedHint:
    'Nicht gelistete Spaces erscheinen nie auf der Startseite. Einschalten, damit er nach dem Veröffentlichen sichtbar wird.',
  dashShowOnHomeOn: 'Auf der Startseite sichtbar',
  dashShowOnHomeOff: 'Von der Startseite ausgeblendet',
  dashHiddenFromHome: 'Veröffentlicht · nicht auf der Startseite',
  dashOpenPublic: 'Öffentlichen Link öffnen',
  dashOpenUnlisted: 'Nicht gelisteten Link öffnen',
  dashContext: 'Kontextbeschreibung (#Hashtags erlaubt)',
  dashPreviewThumbnail: 'Vorschaubild',
  dashPreviewChoose: 'Bild wählen',
  dashPreviewUseDefault: 'Listen-Standard verwenden',
  dashPreviewSaved: 'Vorschaubild gespeichert',
  dashPreviewEmpty: 'Noch keine Fotos in diesem Space. Bilder in der App hinzufügen und erneut veröffentlichen.',
  dashPreviewSaving: 'Speichern…',
  dashFirstPublished: 'Erstmals veröffentlicht',
  dashLastUpdated: 'Zuletzt aktualisiert',
  dashUpdatedShort: 'Aktualisiert {date}',
  dashReplaceZip: 'Aus ZIP ersetzen',
  dashSpaceSaved: 'Space gespeichert',
  dashDeleteSpace: 'Space löschen',
  dashDeleteSpaceBody: 'Diesen Space dauerhaft löschen? Der öffentliche Slug kann nicht wiederverwendet werden.',
  dashDeletePermanently: 'Endgültig löschen',
  dashSpaceDeleted: 'Space gelöscht',
  dashChooseZip: 'Zuerst eine ZIP wählen.',
  dashReplaceSpace: 'Space ersetzen',
  dashReplaceSpaceBody:
    'Den gesamten Inhalt dieses Space ersetzen? Titel und Link bleiben. Die Discover-Listung braucht eine neue Freigabe.',
  dashZipFailed: 'ZIP-Publisher konnte nicht geladen werden. Bitte neu laden.',
  dashSpaceReplaced: 'Space ersetzt. Discover-Listung braucht eine neue Freigabe.',
  dashUnlist: 'Auslisten',
  dashUnlisted: 'Space nicht gelistet (weiterhin online).',
  dashPublishedAgain: 'Space erneut veröffentlicht.',
  dashCollSaved: 'Sammlung gespeichert',
  dashDeleteColl: 'Sammlung löschen',
  dashDeleteCollBody: 'Diese Sammlung löschen?',
  dashCollDeleted: 'Sammlung gelöscht',
  dashRemovedFav: 'Aus Favoriten entfernt',
  dashHeartFirst: 'Zuerst eine Vorlage merken, dann können Favoriten auf dem Profil erscheinen.',
  dashFavName: 'Lieblingsvorlagen',
  dashFavPublic: 'Lieblingsvorlagen erscheinen auf Ihrem öffentlichen Profil.',
  dashFavPrivate: 'Lieblingsvorlagen bleiben privat.',
  dashAvatarUpdated: 'Avatar aktualisiert',
  dashPhotoRemoved: 'Profilfoto entfernt',
  playgroundEmpty: 'Noch keine öffentlichen Spaces.',
  playgroundEdit: 'Layout bearbeiten',
  playgroundDoneEdit: 'Bearbeiten beenden',
  playgroundReset: 'Als Raster zurücksetzen',
  playgroundSave: 'Layout speichern',
  playgroundHint:
    'Ziehen zum Platzieren. Der Griff unten rechts ändert Breite und Höhe frei; Antippen wechselt in Drehen. Am orangenen Mittelpunkt ziehen, um die Startgeschwindigkeit als Pfeil zu zeichnen. Die Physik-Vorschau zeigt Schwerkraft, Absprung und Dichte.',
  playgroundReplay: 'Positionen zurücksetzen',
  playgroundPhysicsOff: 'Physik aus',
  playgroundPhysicsOn: 'Physik ein',
  playgroundGravity: 'Schwerkraft',
  playgroundGravityZero: 'Null',
  playgroundDrag: 'Wasserwiderstand',
  playgroundBounce: 'Absprung',
  playgroundCanvasHeight: 'Canvas-Höhe',
  playgroundThisTile: 'Ausgewählter Space',
  playgroundItemDrag: 'Kachel-Widerstand',
  playgroundItemBounce: 'Material-Absprung',
  playgroundItemDensity: 'Dichte',
  playgroundItemFixed: 'Festes Hindernis',
  playgroundPreviewOn: 'Physik-Vorschau',
  playgroundPreviewOff: 'Vorschau pausieren',
  playgroundResize: 'Größe oder Drehung',
  shareYours: 'Teilen Sie Ihres mit',
  shareHeart: 'Diesen Space merken',
  shareGoBack: 'Eine Ebene zurück',
  shareMenuOpen: 'Menü',
  shareMenuClose: 'Menü schließen',
  sharePathInfo: 'Space-Info',
  shareNoDescription: 'Keine Beschreibung',
  shareContaindSpaces: 'Containd Spaces',
  shareCanvasPath: 'Canvas-Pfad',
  shareRelatedTemplates: 'Verwandte Vorlagen',
  shareTemplatesLabel: 'Vorlagen:',
  shareNoCanvas: 'Keine Canvas-Daten für diese Ebene.',
  sharePublishedWith: 'Veröffentlicht mit',
  shareBakeWarn: (freezeVersion, count) =>
    `Diese Freigabe ist Freeze v${freezeVersion}, aber ${count} Vorlagen-Element${
      count === 1 ? '' : 'e'
    } haben keine bakedDescriptors — sie erscheinen als generische Kacheln. Erneut aus Containd exportieren (Share-Sidecar muss Vorlagen backen), dann neu importieren.`,
  shareViewOne: '1 Aufruf',
  shareViewsMany: '{n} Aufrufe',
  shareViews: (n) => `${n} Aufrufe`,
  shareCopyLink: 'Link kopieren',
  shareCopied: 'Kopiert',
  shareThisPage: 'Diese Seite teilen',
  sharePinPinterest: 'Auf Pinterest pinnen',
  sharePrevLayout: 'Vorheriges Layout',
  shareNextLayout: 'Nächstes Layout',
  shareViewItem: 'Element ansehen',
  shareEnterCanvas: 'Canvas öffnen',
  sharePhotoViewer: 'Fotobetrachter',
  shareZoom: 'Canvas-Zoom',
  shareZoomOut: 'Verkleinern',
  shareResetView: 'Ansicht zurücksetzen',
  shareZoomIn: 'Vergrößern',
  shareRefocus: 'Auf Inhalt fokussieren',
  shareReportTitle: 'Diesen Space melden',
  shareReportHint: 'Meldungen prüft Containd. Ihr Konto wird mit der Meldung gespeichert.',
  shareReportThanks: 'Danke — wir haben Ihre Meldung erhalten für',
  shareSending: 'Senden…',
  shareLead: 'Geteilte Spaces von Containd',
  shareFooterChoices: 'Datenschutz-Einstellungen',
  shareSubmitReport: 'Meldung senden',
  shareCustomReasonPh: 'Kurzer eigener Grund…',
  shareDetailsPh: 'Details für Moderatoren…',
  shareReasonLegend: 'Grund',
  shareAlreadyReported: 'Sie haben dies kürzlich bereits gemeldet.',
  shareReportFailed: 'Meldung konnte nicht gesendet werden. Nach der Anmeldung erneut versuchen.',
  shareCustomReasonError: 'Bitte einen kurzen eigenen Grund eingeben.',
  shareDetailsError: 'Bitte ein paar Details für Moderatoren hinzufügen.',
  shareViaDevice: 'Über Gerät teilen…',
  shareLabel: 'Teilen',
  shareBack: '← Zurück',
  shareReasonSpam: 'Spam oder Werbung',
  shareReasonAbuse: 'Belästigung oder Missbrauch',
  shareReasonIllegal: 'Illegaler Inhalt',
  shareReasonCopyright: 'Urheberrecht / IP-Verletzung',
  shareReasonPrivacy: 'Datenschutzverletzung',
  shareReasonMisleading: 'Irreführend oder Betrug',
  shareReasonOther: 'Sonstiges',
  sharePrevPhoto: 'Vorheriges Foto',
  shareNextPhoto: 'Nächstes Foto',
  externalLinkConfirmTitle: 'Diesen Space verlassen?',
  externalLinkConfirmBody:
    'Sie öffnen gleich einen externen Link auf {host}. Diese Seite gehört nicht zu Containd Spaces.',
  externalLinkConfirmContinue: 'Weiter',
  externalLinkConfirmCancel: 'Abbrechen',
  inboxAria: 'Posteingang',
  inboxEmpty: 'Keine Nachrichten',
  inboxLater: 'Später',
  inboxDismiss: 'Schließen',
  inboxUnread: 'Ungelesen',
  inboxHistory: 'Früher',
  inboxDelete: 'Löschen',
};

const JP: ExtraUiStrings = {
  ...EN,
  confirm: '確認',
  cancel: 'キャンセル',
  close: '閉じる',
  save: '保存',
  dialogFailed: 'ダイアログを読み込めませんでした。再読み込みしてください。',
  requestFailed: 'リクエストに失敗しました',
  consentAria: 'プライバシー設定',
  consentBanner:
    'このサイトの運営に必要なCookieを使用します。任意の分析とYouTube再生には同意が必要です。詳細は',
  consentAcceptAll: 'すべて許可',
  consentNecessaryOnly: '必須のみ',
  consentCustomize: 'カスタマイズ',
  consentBack: '戻る',
  consentDetails: '詳細はプライバシーポリシー',
  consentNecessary: '必須',
  consentAnalytics: '分析',
  consentYoutube: 'YouTube',
  consentPrivacyChoices: 'プライバシー設定',
  consentPrivacyPolicy: 'プライバシーポリシー',
  consentTerms: '利用規約',
  consentImprint: 'インプリント',
  legalContactTitle: 'お問い合わせ / DSA — Containd Spaces',
  legalContactDescription: 'DSA通知、TCO命令、法的照会のContaind連絡先。',
  legalContactHeading: 'お問い合わせ / DSA',
  legalContactOperated: 'Containdの公開スペースの運営者は',
  legalAbuseHeading: '不正利用・違法コンテンツの通知',
  legalAbuseBody: 'または',
  legalContactHeading2: '法務連絡先',
  legalContactInbox: '（このソフトローンチでは不正利用と同じ受信箱です）。',
  legalDsaHeading: 'DSA / TCO',
  legalDsaNotice: 'EUデジタルサービス法に基づく通知:',
  legalTcoNotice: 'ドイツ電気通信メディア法（TCO）の削除命令の送付先',
  legalPoliciesHeading: 'ポリシー',
  legalNoticeForm: '公開通知フォーム',
  legalReportTitle: '違法コンテンツを報告 — Containd Spaces',
  legalReportDescription: 'containd.space上の違法とされるコンテンツをContaindに通知します。',
  legalReportHeading: '違法コンテンツを報告',
  legalReportIntro:
    'このフォームでContaind（Next Level Order Engineering UG）にcontaind.space上の違法とされるコンテンツを通知できます。通知は次の宛先でも受け付けます',
  legalReportUrlLabel: 'コンテンツのURL',
  legalReportCategory: 'カテゴリ',
  legalCatIllegal: '違法とされるコンテンツ',
  legalCatCopyright: '著作権 /知的財産',
  legalCatAbuse: '不正利用 / 嫌がらせ',
  legalCatPrivacy: 'プライバシー / 個人データ',
  legalCatSpam: 'スパム / 詐欺',
  legalCatOther: 'その他',
  legalReportDetails: '説明 / 根拠',
  legalReportDetailsPh: '違法または対応が必要な理由を書いてください…',
  legalReportName: 'お名前',
  legalReportEmail: 'メールアドレス',
  legalReportSlug: '共有スラッグ（任意）',
  legalGoodFaith:
    'この通知の内容は正確であり、提出する権限がある（該当する場合）と誠実に信じています。',
  legalSubmitNotice: '通知を送信',
  legalTurnstileRequired: '確認チェックを完了してください。',
  legalNoticeReceived: '通知を受け取りました。確認メールの送信先',
  loginBlurbGuest:
    'これはContaindアカウントです。Containdアプリ（iOS、Android、Web）とcontaind.spaceおよびカタログで同じIDです。Google、Apple、またはメールでサインインしてください。ここでアカウントを作ると同じContaindアカウントになります。',
  loginBlurbSigned:
    'これはContaindアカウントです。すべてのプラットフォームのContaindアプリとcontaind.spaceおよびカタログで同じIDです。',
  loginAgeConfirm: '16歳以上であることを確認します。',
  loginContinueGoogle: 'Googleで続ける',
  loginContinueApple: 'Appleで続ける',
  loginOrEmail: 'またはメール',
  loginEmailMode: 'メールモード',
  loginCreateAccount: 'アカウント作成',
  loginDisplayNameOptional: '表示名（任意）',
  loginEmail: 'メール',
  loginPassword: 'パスワード',
  loginConfirmPassword: 'パスワード確認',
  loginSignInEmail: 'メールでサインイン',
  loginForgotPassword: 'パスワードをお忘れですか？',
  loginPublicProfile: '公開プロフィール',
  loginPublicAlias: '公開エイリアス（URLスラッグ）',
  loginAliasTitle: '2–32文字: 小文字、数字、ハイフン',
  loginDisplayName: '表示名',
  loginBio: '自己紹介',
  loginProfilePhoto: 'プロフィール写真',
  loginPhotoHint:
    '4:5の縦写真1枚（400×500）。再アップロードで差し替わります。アカウントメニューは同じ画像の丸い切り抜きを使います。',
  loginRemovePhoto: '写真を削除',
  loginProfilePublic: 'プロフィールを公開する',
  loginPrivateDefault: '初期状態は非公開です。公開プロフィールは次に表示されます',
  loginPublicUrl: '公開プロフィールURL',
  loginCopy: 'コピー',
  loginSaveProfile: 'プロフィールを保存',
  loginUnsavedChanges: '未保存の変更',
  loginYourSpaces: 'あなたのスペース',
  loginYourCollections: 'あなたのコレクション',
  loginNewCollection: '新しいコレクション',
  loginFavTemplates: 'お気に入りテンプレート',
  loginShowFavOnProfile: '公開プロフィールにお気に入りテンプレートを表示',
  loginAchievementsHeading: 'プロフィールの実績',
  loginAchievementsHint: 'デフォルトはオフ。表示されるのは画像と名前のみで、解除条件は表示されません。',
  loginShowBadgesOnProfile: '公開プロフィールにバッジを表示',
  loginShowClassOnProfile: '公開プロフィールにクラスを表示',
  loginShowAchievementGroup: 'グループを表示:',
  loginAchievementsLoadError: '実績の設定を読み込めませんでした。ページを更新してもう一度お試しください。',
  loginFeaturedHeading: '写真の横に表示',
  loginFeaturedHint: 'ヘッダーグリッド用に最大18個の丸いバッジを選べます（1行3個）。',
  loginFeaturedCount: 'おすすめ {{count}} / {{max}}',
  loginFeaturedMax: 'おすすめバッジは最大18個までです。',
  loginAchGroupsFold: '表示するバッジグループ',
  loginAchFeaturedFold: 'おすすめバッジ',
  loginFavEmpty: 'カタログでテンプレートをお気に入りにすると、ここに保存されます。',
  loginPublishExport: 'エクスポートから公開',
  loginZipHint:
    'ContaindのエクスポートZIP（share/manifest.json または freeze.json）をアップロードします。新しい公開ページが作られます。既存ページを更新するには上で選び、ZIPで置換してください。タイトルとリンクは維持され、Discoverの再承認が必要です。',
  loginZipContext: 'コンテキスト説明（#ハッシュタグ可）',
  loginZipContextPh: 'このスペースの用途…',
  loginPublishZip: 'ZIPを公開',
  loginDeleteHeading: 'Containdアカウントを削除',
  loginDeleteLead: 'Containdアカウントが削除されます',
  loginDeleteBody:
    'このアカウントのサーバーデータはすべて完全に削除されます。クラウド同期、公開スペース、プロフィール、アバター、コレクション、アップロード、AIリクエストとクレジット、分析ID、プッシュ登録。請求記録は最大32日保持された後に削除されます。上の公開スペース1件の削除とは異なり、アカウント全体が消えます。元に戻せません。同じメールで再登録できます。端末だけに保存されたリストは変わりません。',
  loginTypeDelete: '確認のため DELETE と入力',
  loginDeleteButton: 'アカウントを完全に削除',
  loginSignedInAs: 'サインイン中',
  loginSignInProviders: '公開プロフィールを管理するには、Google、Apple、またはメールでサインインしてください。',
  loginAgeAttestAgain: '16歳以上であることを確認して、もう一度サインインしてください。',
  loginPasswordsMismatch: 'パスワードが一致しません。',
  loginPasswordMin: 'パスワードは6文字以上にしてください。',
  loginResetSent: 'パスワード再設定メールを送信しました（アカウントがある場合）。',
  loginDeleteAccountTitle: 'アカウント削除',
  loginDeleteAccountBody:
    'Containdアカウントとサーバーデータが完全に削除されます。確認のため DELETE と入力してください。',
  loginSavedPublic: '保存しました。公開ページ:',
  loginSavedPrivate: '保存しました。「プロフィールを公開する」をオンにするまで非公開です。',
  loginRemovePhotoTitle: '写真を削除',
  loginRemovePhotoBody: 'プロフィール写真を削除しますか？',
  loginUrlCopied: 'プロフィールURLをコピーしました。',
  loginUrlCopyFail: 'URLをコピーできませんでした。',
  loginAliasFormat: '小文字・数字・ハイフンで2–32文字。',
  loginAliasAvailable: '利用できます',
  loginAliasCurrent: '現在のユーザー名です。',
  loginAliasTaken: 'そのユーザー名は使われています。',
  loginCropHint: 'ドラッグして4:5の縦写真を枠に合わせる',
  loginCropFailed: '切り抜きUIを読み込めませんでした。再読み込みしてください。',
  loginUsePhoto: '写真を使う',
  loginEnterCollectionName: 'コレクション名を入力してください。',
  loginSaving: '保存中…',
  loginCreateFailed: 'コレクションを作成できませんでした',
  loginChooseZip: 'サインインして、先にZIPを選んでください。',
  loginZipFailed: 'ZIP公開機能を読み込めませんでした。再読み込みしてください。',
  loginPublished: '公開しました:',
  loginAuthInvalidEmail: '有効なメールアドレスを入力してください。',
  loginAuthDisabled: 'このアカウントは無効です。',
  loginAuthNotFound: 'そのメールのアカウントはありません。',
  loginAuthWrongPassword: 'パスワードが違います。',
  loginAuthInvalidCred: 'メールまたはパスワードが違います。',
  loginAuthEmailInUse: 'そのメールのアカウントは既にあります。サインインしてください。',
  loginAuthWeakPassword: 'パスワードは6文字以上にしてください。',
  loginAuthTooMany: '試行が多すぎます。しばらくしてからやり直してください。',
  loginAuthMissingPassword: 'パスワードを入力してください。',
  loginAuthRecentLogin:
    '安全のため、サインアウトして再サインインしてからアカウント削除を試してください。',
  loginAliasTakenErr: 'そのユーザー名は使われています。別の名前を試してください。',
  loginInvalidAlias: 'エイリアスは2–32文字で、小文字・数字・ハイフンです。',
  loginAliasQuota: 'エイリアスは90日に2回まで変更できます。後でもう一度お試しください。',
  loginNameQuota: '表示名は30日に4回まで変更できます。後でもう一度お試しください。',
  loginHandoffFailed: 'containd.spaceにはサインインしましたが、カタログ連携に失敗しました:',
  cardHeartSpace: 'このスペースをお気に入り',
  cardAddToCollection: 'コレクションに追加…',
  cardReportSpace: 'このスペースを報告',
  cardShareSpace: 'スペースを共有',
  cardReportTitle: 'このスペースを報告',
  cardReason: '理由',
  cardReasonSpam: 'スパムまたは広告',
  cardReasonAbuse: '嫌がらせまたは不正利用',
  cardReasonIllegal: '違法コンテンツ',
  cardReasonCopyright: '著作権 / 知的財産の侵害',
  cardReasonPrivacy: 'プライバシー侵害',
  cardReasonMisleading: '誤解を招く、または詐欺',
  cardCustomReason: '理由を記入',
  cardCustomReasonPh: '短いラベル…',
  cardDetails: '詳細',
  cardDetailsPh: 'モデレーターに伝えてほしいこと',
  cardSubmitReport: '報告を送信',
  cardEnterCustom: '短い独自の理由を入力してください。',
  cardAddDetails: 'モデレーター向けに詳細を少し追加してください。',
  cardAlreadyReported: '最近すでに報告済みです。',
  cardReportFail: '報告を送信できませんでした。',
  cardReportThanks: 'ありがとう — 報告を送信しました。',
  cardNewCollection: '新しいコレクション',
  cardName: '名前',
  cardFavourites: 'お気に入り',
  cardDescOptional: '説明（任意、#ハッシュタグ可）',
  cardCollectionForPh: 'このコレクションの用途…',
  cardMakePublic: 'プロフィールでコレクションを公開する',
  cardCreateAdd: '作成して追加',
  cardCreateFail: 'コレクションを作成できませんでした。',
  cardAddedTo: '追加先',
  cardAddToTitle: 'コレクションに追加',
  cardThisShare: 'この共有',
  cardCreateNewCollection: '新しいコレクションを作成…',
  cardCouldNotSave: '保存できませんでした。',
  cardSavedCollection: 'コレクションに保存しました。',
  cardLinkCopied: 'リンクをコピーしました',
  cardCopyFail: 'リンクをコピーできませんでした',
  cardLoadCollectionsFail: 'コレクションを読み込めませんでした。',
  cardCollection: 'コレクション',
  dashPublished: '公開済み',
  dashPendingDiscover: '公開済み · Discover審査中',
  dashNoSpaces: 'スペースはまだありません — Containdアプリから公開してください。',
  dashNoSpacesMatch: '一致するスペースがありません — 検索またはフィルターをクリアしてください。',
  dashNoCollections: 'コレクションはまだありません — 見つけるからスペースを追加。',
  dashSearchSpaces: 'タイトルまたはスラッグで検索',
  dashSearchSpacesLabel: 'スペースを検索',
  dashFilterSpaces: 'スペースを絞り込む',
  dashFilterAll: 'すべて',
  dashFilterPending: '審査中',
  dashFilterFeatured: '注目',
  dashFilterUnlisted: '非公開',
  dashSpaceTitle: 'タイトル',
  dashSpaceTitleHint:
    'タイトルを変更すると Discover の再承認が必要になることがあります。公開リンクはそのままです。',
  dashUseListName: 'リスト名を使う',
  dashTitleRequired: 'タイトルを入力してください。',
  dashShowOnProfile: 'プロフィールに表示',
  dashShowOnProfileHint:
    'オフにすると、スペースは公開のままプロフィールには出ません。',
  dashShowOnProfileUnlistedHint:
    '非公開のスペースはプロフィールに出ません。公開後に表示するにはオンにしてください。',
  dashShowOnProfileOn: 'プロフィールに表示中',
  dashShowOnProfileOff: 'プロフィールから非表示',
  dashHiddenFromProfile: 'プロフィール非表示',
  dashShowOnHome: 'ホームページに表示',
  dashShowOnHomeHint:
    'オフにすると、スペースは公開のまま Discover にも出ますが、containd.space のホームページには出ません。',
  dashShowOnHomeUnlistedHint:
    '非公開のスペースはホームページに出ません。公開後に表示するにはオンにしてください。',
  dashShowOnHomeOn: 'ホームページに表示中',
  dashShowOnHomeOff: 'ホームページから非表示',
  dashHiddenFromHome: '公開済み · ホームページ非表示',
  dashOpenPublic: '公開リンクを開く',
  dashOpenUnlisted: '非公開リンクを開く',
  dashContext: 'コンテキスト説明（#ハッシュタグ可）',
  dashPreviewThumbnail: 'プレビューサムネイル',
  dashPreviewChoose: '画像を選ぶ',
  dashPreviewUseDefault: 'リストのデフォルトを使用',
  dashPreviewSaved: 'プレビューサムネイルを保存しました',
  dashPreviewEmpty: 'このスペースに写真がありません。アプリで画像を追加して再公開してください。',
  dashPreviewSaving: '保存中…',
  dashFirstPublished: '初回公開',
  dashLastUpdated: '最終更新',
  dashUpdatedShort: '更新 {date}',
  dashReplaceZip: 'ZIPで置換',
  dashSpaceSaved: 'スペースを保存しました',
  dashDeleteSpace: 'スペースを削除',
  dashDeleteSpaceBody: 'このスペースを完全に削除しますか？公開スラッグは再利用できません。',
  dashDeletePermanently: '完全に削除',
  dashSpaceDeleted: 'スペースを削除しました',
  dashChooseZip: '先にZIPを選んでください。',
  dashReplaceSpace: 'スペースを置換',
  dashReplaceSpaceBody:
    'このスペースの内容をすべて置き換えますか？タイトルとリンクはそのままです。Discover掲載は再承認が必要です。',
  dashZipFailed: 'ZIP公開機能を読み込めませんでした。再読み込みしてください。',
  dashSpaceReplaced: 'スペースを置換しました。Discover掲載は再承認が必要です。',
  dashUnlist: '非公開にする',
  dashUnlisted: 'スペースを非公開にしました（オンラインのまま）。',
  dashPublishedAgain: 'スペースを再公開しました。',
  dashCollSaved: 'コレクションを保存しました',
  dashDeleteColl: 'コレクションを削除',
  dashDeleteCollBody: 'このコレクションを削除しますか？',
  dashCollDeleted: 'コレクションを削除しました',
  dashRemovedFav: 'お気に入りから削除しました',
  dashHeartFirst: '先にテンプレートをお気に入りにすると、プロフィールに表示できます。',
  dashFavName: 'お気に入りテンプレート',
  dashFavPublic: 'お気に入りテンプレートが公開プロフィールに表示されます。',
  dashFavPrivate: 'お気に入りテンプレートは非公開のままです。',
  dashAvatarUpdated: 'アバターを更新しました',
  dashPhotoRemoved: 'プロフィール写真を削除しました',
  playgroundEmpty: '公開スペースはまだありません。',
  playgroundEdit: 'レイアウトを編集',
  playgroundDoneEdit: '編集を終了',
  playgroundReset: 'グリッドに戻す',
  playgroundSave: 'レイアウトを保存',
  playgroundHint:
    'ドラッグで配置。右下ハンドルで縦横を自由にリサイズ、タップで回転。中央のオレンジハンドルを引いて初速の矢印を描きます。物理プレビューで重力・反発・密度を確認できます。',
  playgroundReplay: '位置をリセット',
  playgroundPhysicsOff: '物理をオフ',
  playgroundPhysicsOn: '物理をオン',
  playgroundGravity: '重力',
  playgroundGravityZero: 'ゼロ',
  playgroundDrag: '水の抵抗',
  playgroundBounce: '反発',
  playgroundCanvasHeight: 'キャンバスの高さ',
  playgroundThisTile: '選択中のスペース',
  playgroundItemDrag: 'タイルの抵抗',
  playgroundItemBounce: '材質の反発',
  playgroundItemDensity: '密度',
  playgroundItemFixed: '固定の障害物',
  playgroundPreviewOn: '物理をプレビュー',
  playgroundPreviewOff: 'プレビューを一時停止',
  playgroundResize: 'サイズまたは回転',
  shareYours: 'あなたのも公開 —',
  shareHeart: 'このスペースをお気に入り',
  shareGoBack: '一つ上の階層へ',
  shareMenuOpen: 'メニュー',
  shareMenuClose: 'メニューを閉じる',
  sharePathInfo: 'スペース情報',
  shareNoDescription: '説明なし',
  shareContaindSpaces: 'Containd Spaces',
  shareCanvasPath: 'キャンバス経路',
  shareRelatedTemplates: '関連テンプレート',
  shareTemplatesLabel: 'テンプレート:',
  shareNoCanvas: 'この階層のキャンバスデータがありません。',
  sharePublishedWith: '公開元',
  shareBakeWarn: (freezeVersion, count) =>
    `この共有は freeze v${freezeVersion} ですが、テンプレート項目 ${count} 件に bakedDescriptors がありません。一般タイルとして描画されます。Containdから再エクスポート（シェアのサイドカーでテンプレートをベイク）して再インポートしてください。`,
  shareViewOne: '1 回閲覧',
  shareViewsMany: '{n} 回閲覧',
  shareViews: (n) => `${n} 回閲覧`,
  shareCopyLink: 'リンクをコピー',
  shareCopied: 'コピーしました',
  shareThisPage: 'このページを共有',
  sharePinPinterest: 'Pinterestにピン',
  sharePrevLayout: '前のレイアウト',
  shareNextLayout: '次のレイアウト',
  shareViewItem: 'アイテムを見る',
  shareEnterCanvas: 'キャンバスに入る',
  sharePhotoViewer: '写真ビューア',
  shareZoom: 'キャンバスズーム',
  shareZoomOut: '縮小',
  shareResetView: '表示をリセット',
  shareZoomIn: '拡大',
  shareRefocus: '内容にフォーカス',
  shareReportTitle: 'このスペースを報告',
  shareReportHint: '報告はContaindが確認します。アカウントは報告に記録されます。',
  shareReportThanks: 'ありがとう — 次の報告を受け取りました',
  shareSending: '送信中…',
  shareLead: 'Containdから公開されたスペース',
  shareFooterChoices: 'プライバシー設定',
  shareSubmitReport: '報告を送信',
  shareCustomReasonPh: '短い独自の理由…',
  shareDetailsPh: 'モデレーター向けの詳細…',
  shareReasonLegend: '理由',
  shareAlreadyReported: '最近すでに報告済みです。',
  shareReportFailed: '報告を送信できませんでした。サインイン後にもう一度お試しください。',
  shareCustomReasonError: '短い独自の理由を入力してください。',
  shareDetailsError: 'モデレーター向けに少し詳細を追加してください。',
  shareViaDevice: 'デバイスで共有…',
  shareLabel: '共有',
  shareBack: '← 戻る',
  shareReasonSpam: 'スパムまたは広告',
  shareReasonAbuse: '嫌がらせまたは虐待',
  shareReasonIllegal: '違法なコンテンツ',
  shareReasonCopyright: '著作権 / IP侵害',
  shareReasonPrivacy: 'プライバシー侵害',
  shareReasonMisleading: '誤解を招く / 詐欺',
  shareReasonOther: 'その他',
  sharePrevPhoto: '前の写真',
  shareNextPhoto: '次の写真',
  externalLinkConfirmTitle: 'このスペースを離れますか？',
  externalLinkConfirmBody:
    '{host} の外部リンクを開こうとしています。このサイトは Containd Spaces の一部ではありません。',
  externalLinkConfirmContinue: '続ける',
  externalLinkConfirmCancel: 'キャンセル',
  inboxAria: '受信トレイ',
  inboxEmpty: 'メッセージはありません',
  inboxLater: 'あとで',
  inboxDismiss: '閉じる',
  inboxUnread: '未読',
  inboxHistory: '以前',
  inboxDelete: '削除',
};

const EXTRA_BY_LOCALE: Record<string, ExtraUiStrings> = {
  en: EN,
  de: DE,
  jp: JP,
};

export function extraUi(locale: string): ExtraUiStrings {
  const key = locale.toLowerCase().split('-')[0] ?? 'en';
  return EXTRA_BY_LOCALE[key] ?? EN;
}

export function pickStringFields(obj: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

/** Flat string map for `window.__CONTAIND_UI__` (functions omitted). */
export function publicUiStrings(locale: string): Record<string, string> {
  return {
    ...pickStringFields(catalogUi(locale)),
    ...pickStringFields(listsUi(locale)),
    ...pickStringFields(extraUi(locale)),
  };
}

export function renderPublicUiScript(locale: string): string {
  const json = JSON.stringify(publicUiStrings(locale)).replace(/</g, '\\u003c');
  return `<script>window.__CONTAIND_UI__=${json};</script>`;
}

export function resolvePublicUiLocale(raw?: string | null): CatalogSiteLocale {
  const key = String(raw || 'en')
    .toLowerCase()
    .split('-')[0];
  return (CATALOG_SITE_LOCALES as readonly string[]).includes(key || '')
    ? (key as CatalogSiteLocale)
    : 'en';
}

export function extraUiForTests() {
  return { en: EN, de: DE, jp: JP };
}
